import { useExpertUniversityConfiguration, useExpertUniversityStore } from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import { showNotification } from "@mantine/notifications";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLogger } from "@/sdk/logging/LoggerProvider";

import type {
    EvaluationResultsErrorResponse,
    SessionEvaluationSkippedStatus,
    TranscriptContent,
} from "../api/evaluations/types";

import { evaluateTrainingSessionBySessionId, getEvaluationResultsBySessionId } from "../api/requests";
import { TrainingSessionChallengeResultStatus, TrainingSessionChallengeStatusEnum } from "../types";
import { isEvaluationTranscriptContent } from "../utils";

// Statuses the `session/{sessionId}/evaluate` endpoint returns when there is nothing to render: the
// evaluation was already saved, or the session was ineligible. These carry no EvaluationDetails, so we
// simply clear the loading state and stay on the learning-blocks view (no results page, no session save).
const SKIPPED_STATUSES: readonly SessionEvaluationSkippedStatus[] = ["already_completed", "too_short", "no_transcript"];

const isSkippedStatus = (status: string): status is SessionEvaluationSkippedStatus =>
    (SKIPPED_STATUSES as readonly string[]).includes(status);

const EVALUATION_ERROR_RESPONSE: EvaluationResultsErrorResponse = Object.freeze({
    ErrorCode: "EVALUATION_ERROR",
    Message: "Failed to fetch evaluation results",
});

export interface UseTrainingEvaluationResults {
    evaluationResults: TranscriptContent | EvaluationResultsErrorResponse | undefined;
    isPending: boolean;
    clearEvaluationResults: () => void;
    /**
     * Loads the already-stored evaluation for a past session (the tile's "View results" click) and
     * drives the same loading/results/error state as the live post-call flow. This is distinct from
     * the `session/{sessionId}/evaluate` endpoint the mount-time listener uses: that generates a fresh
     * evaluation on hangup, whereas this reads a persisted result and never re-triggers evaluation.
     */
    viewResultsBySessionId: (sessionId: string) => Promise<void>;
}

/**
 * Owns the ASCEND-pilot evaluation-results flow: it listens for the `trigger-evaluation-results`
 * event, calls the new `session/{sessionId}/evaluate` endpoint, and drives the loading/results/error
 * state that `ExpertUniversityLearningBlocks` renders. Mirrors the FullStory + logger events in
 * `ExpertUniversityHome` on a completed evaluation, but against the new endpoint. The endpoint already
 * persists the evaluation to the `ai-assisted-training` DS record server-side, so this hook no longer
 * writes the training session from the client. Because only the ASCEND experience mounts
 * `ExpertUniversityLearningBlocks`, wiring this hook there inherently scopes the behavior to the pilot.
 */
export function useTrainingEvaluationResults(): UseTrainingEvaluationResults {
    const logger = useLogger({ module: "Expert University Learning Blocks" });
    const { taskInSandbox } = useExpertUniversityConfiguration();

    const [evaluationResults, setEvaluationResults] = useState<
        TranscriptContent | EvaluationResultsErrorResponse | undefined
    >();
    const [isPending, setIsPending] = useState(false);

    // The subscription effect runs once (empty deps), so read the latest sandbox flag through a ref
    // rather than capturing the mount-time value in the async handler's closure.
    const taskInSandboxRef = useRef(taskInSandbox);
    taskInSandboxRef.current = taskInSandbox;

    useEffect(() => {
        const eventBus = useExpertUniversityStore.getState().eventBus;
        const off = eventBus?.on("trigger-evaluation-results", async (event) => {
            // After ending a 'call' in the sandbox environment there are no evaluation results to fetch.
            if (taskInSandboxRef.current) {
                setEvaluationResults(undefined);
                return;
            }
            setIsPending(true);

            try {
                const result = await evaluateTrainingSessionBySessionId(event.sessionId);

                // Success: a completed evaluation carries the TranscriptContent payload (has
                // EvaluationDetails, so it satisfies isEvaluationTranscriptContent). The status
                // discriminant narrows the union to the completed shape first so the guard type-checks.
                if (result.status === "completed" && isEvaluationTranscriptContent(result)) {
                    // The freshly-triggered evaluation response may omit ChallengeId. Fall back to the
                    // profileId from the event so the updated-call-flow checklist resolves correctly.
                    result.ChallengeId ??= event.profileId;

                    const evaluationOutcome = result.EvaluationDetails.Results?.toLocaleLowerCase();
                    const challengeStatus =
                        evaluationOutcome === TrainingSessionChallengeResultStatus.CLEARED
                            ? TrainingSessionChallengeStatusEnum.COMPLETE
                            : TrainingSessionChallengeStatusEnum.NOT_COMPLETE;

                    // The new endpoint's EvaluationDetails omits the `logs` block, so behaviorsPresent /
                    // maxBehaviors are unavailable here and are intentionally not reported.
                    trackFullStoryEvent("Evaluation Results Received", {
                        sessionId: event.sessionId,
                        profileId: event.profileId,
                        evaluationResults: evaluationOutcome,
                    });
                    logger.info(
                        {
                            challengeStatus,
                            evaluationResults: evaluationOutcome,
                            sessionId: event.sessionId,
                            profileId: event.profileId,
                        },
                        "Triggering evaluation results",
                    );

                    setEvaluationResults(result);
                    setIsPending(false);
                    return;
                }

                // Skipped: no evaluation payload. Clear loading and stay on the learning-blocks view.
                if (isSkippedStatus(result.status)) {
                    logger.info(
                        { status: result.status, sessionId: event.sessionId },
                        "Evaluation skipped; no results to display",
                    );
                    showNotification({
                        id: "evaluation-skipped",
                        position: "top-center",
                        withCloseButton: true,
                        autoClose: 4000,
                        message: "No evaluation results are available for this session.",
                    });
                    setEvaluationResults(undefined);
                    setIsPending(false);
                    return;
                }

                // Everything else funnels here (not_found / server_error / error, or a completed
                // response that unexpectedly lacked its payload). Surface the error overlay.
                logger.info(
                    {
                        error: "error" in result ? result.error : undefined,
                        status: result.status,
                        sessionId: event.sessionId,
                    },
                    "Error triggering evaluation results",
                );
                trackFullStoryEvent("Evaluation Results Error", {
                    sessionId: event.sessionId,
                    profileId: event.profileId,
                });
                setEvaluationResults(EVALUATION_ERROR_RESPONSE);
                setIsPending(false);
            } catch (error: any) {
                logger.info(
                    { error: error?.message, sessionId: event.sessionId },
                    "Error triggering evaluation results",
                );
                trackFullStoryEvent("Evaluation Results Error", {
                    sessionId: event.sessionId,
                    profileId: event.profileId,
                });
                setEvaluationResults(EVALUATION_ERROR_RESPONSE);
                setIsPending(false);
            }
        });

        return () => {
            off?.();
        };
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    const clearEvaluationResults = useCallback(() => {
        setEvaluationResults(undefined);
    }, []);

    const viewResultsBySessionId = useCallback(
        async (sessionId: string) => {
            setIsPending(true);
            try {
                const response = await getEvaluationResultsBySessionId(sessionId);
                const result = response?.items?.[0];
                if (result) {
                    trackFullStoryEvent("Evaluation Results Received", { sessionId });
                    setEvaluationResults(result);
                } else {
                    // The read endpoint swallows its own errors (returns undefined) and may also come
                    // back empty; either way there is nothing to render, so surface the error overlay.
                    logger.info({ sessionId }, "No stored evaluation results found for session");
                    trackFullStoryEvent("Evaluation Results Error", { sessionId });
                    setEvaluationResults(EVALUATION_ERROR_RESPONSE);
                }
            } catch (error: any) {
                // Defensive: the read endpoint swallows its own errors today, but a malformed response
                // (e.g. missing `items`) would otherwise reject a discarded promise silently. Surface the
                // error overlay and keep the failure observable, mirroring the post-call flow above.
                logger.error({ error: error?.message, sessionId }, "Failed to load stored evaluation results");
                trackFullStoryEvent("Evaluation Results Error", { sessionId });
                setEvaluationResults(EVALUATION_ERROR_RESPONSE);
            } finally {
                setIsPending(false);
            }
        },
        [logger],
    );

    return { evaluationResults, isPending, clearEvaluationResults, viewResultsBySessionId };
}
